import React from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import ElapsedTime from 'app/components/general/ElapsedTime';
import {
  FullHTMLLink,
  Props as FullHTMLLinkProps,
} from 'app/components/general/FullHTMLLink';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {StyledListDIV} from 'app/components/general/VersionsSidebar/VersionsSidebar';
import {InfiniteListRTK} from 'app/general/datafetchers/InfiniteListRTK';
import {DocSidebarPagePanels} from 'app/general/structures/DocSidebarPagePanels';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import StateButton from 'app/general/widgets/StateButton';
import {
  convertLocationParamsToFetchParams,
  useAttempts,
  useDeleteAttempt,
  useQuiz,
} from 'app/quizzes/api';
import {SORT_BY_TYPES} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import type {AttemptSerializedType, QuizSerializedType} from 'sweet-types';

export const QuizAttemptSidebar = ({
  canDelete,
  onClose,
  attemptId,
  quizId,
}: {
  canDelete: boolean;
  onClose: () => void;
  attemptId: string;
  quizId: string;
}) => {
  const {data: quiz} = useQuiz(quizId);

  return (
    <InfiniteListRTK
      fetchParams={{
        quiz: quizId,
        ordering: SORT_BY_TYPES.CREATION_DATE,
      }}
      useDataHook={useAttempts}
      paramsConverter={convertLocationParamsToFetchParams}
    >
      {({results: attempts, isLoading, loadNext, hasNext}) =>
        quiz != null ? (
          <AttemptsSidebarComponent
            canDelete={canDelete}
            onClose={onClose}
            quiz={quiz}
            isLoading={isLoading}
            loadMoreAttempts={async () => loadNext()}
            moreToLoad={hasNext}
            attempts={attempts}
            attemptId={attemptId}
          />
        ) : null
      }
    </InfiniteListRTK>
  );
};

const AttemptsSidebarComponent = ({
  canDelete,
  onClose,
  quiz,
  isLoading,
  loadMoreAttempts,
  moreToLoad,
  attempts,
  attemptId,
}: {
  canDelete: boolean;
  onClose: () => void;
  loadMoreAttempts: () => Promise<unknown>;
  quiz: QuizSerializedType;
  moreToLoad: boolean;
  isLoading: boolean;
  attempts: Array<AttemptSerializedType>;
  attemptId: string;
}) => {
  return (
    <DocSidebarPagePanels
      onClose={onClose}
      loading={isLoading}
      title={<span>{gt('Attempts')}</span>}
    >
      <StyledListDIV>
        <ul className="list-group">
          {attempts.map((attempt) => (
            <AttemptItem
              key={attempt.url}
              isSelected={attempt.id == attemptId}
              canDelete={canDelete}
              quiz={quiz}
              attempt={attempt}
            />
          ))}
        </ul>

        {moreToLoad && (
          <div className="text-center my-2 mx-4 pb-2">
            <StateButton
              className="btn btn-secondary btn-sm btn-block"
              busyText={gt('Loading...')}
              onClick={loadMoreAttempts}
            >
              {gt('Load more')}
            </StateButton>
          </div>
        )}
      </StyledListDIV>
    </DocSidebarPagePanels>
  );
};

const AttemptItem = ({
  attempt,
  quiz,
  canDelete,
  isSelected,
}: {
  attempt: AttemptSerializedType;
  quiz: QuizSerializedType;
  canDelete: boolean;
  isSelected: boolean;
}) => {
  const [deleteAttempt] = useDeleteAttempt();
  const createdAtElement = (
    <ElapsedTime
      className="align-items-center"
      tooltipPlacement="top"
      ContainerTag="small"
      timestamp={attempt.created_at}
      wrapTooltip={(text) => <small>{gt('Created %s', text)}</small>}
    />
  );

  const authorNameElement = (
    <span className="small font-weight-bold">
      {attempt.user != null ? attempt.user.name : gt('Deleted user')}
    </span>
  );

  const attemptPercentage = attempt.percentage || 0;
  const quizPassMark = quiz?.pass_mark || 0;

  const passed = attemptPercentage >= quizPassMark;

  const statusBadge = (
    <LazyTooltip
      placement="top"
      tooltip={
        <span>
          {attempt.status === 'pending'
            ? gt('This attempt is currently incomplete.')
            : passed
            ? gt(
                'The score achieved is %s, meeting the required pass mark of %s.',
                `${attemptPercentage}%`,
                `${quizPassMark}%`
              )
            : gt(
                'The score achieved is %s, falling short of the required pass mark of %s',
                `${attemptPercentage}%`,
                `${quizPassMark}%`
              )}
        </span>
      }
    >
      <span
        className={cs('badge float-right', {
          'badge-light': attempt.status === 'pending',
          'badge-dark': !passed && attempt.status != 'pending',
          'badge-success': passed,
        })}
      >
        {attempt.status === 'pending'
          ? gt('Incomplete')
          : passed
          ? gt('Pass')
          : gt('Retry')}
      </span>
    </LazyTooltip>
  );

  const handleAttemptDelete = () => {
    return deleteAttempt(attempt.url).unwrap();
  };

  const actionMenuElement = canDelete ? (
    <ActionsButton
      actions={[
        {
          text: gt('Move to Recycle Bin'),
          icon: 'far fa-trash',
          key: 'delete',
          callback: handleAttemptDelete,
        },
      ]}
    />
  ) : null;

  return (
    <li className={cs('list-group-item p-0 d-flex', {active: isSelected})}>
      <StyledContainerFullHTMLLink
        className={cs('flex-grow-1 pb-2', {selected: isSelected})}
        html_url={`${attempt.take_url}result/`}
        replace={false}
      >
        <React.Fragment>
          <div className="clearfix editor mb-1">{authorNameElement}</div>
          <div className="d-flex justify-content-between align-items-center">
            {createdAtElement}
            <div className="d-flex align-items-center">
              {statusBadge}
              {actionMenuElement}
            </div>
          </div>
        </React.Fragment>
      </StyledContainerFullHTMLLink>
    </li>
  );
};

const StyledContainerFullHTMLLink = styled(
  FullHTMLLink
).attrs<FullHTMLLinkProps>({
  className: 'clearfix px-2 pt-2',
})`
  display: block;
  color: ${colors.textDefault};

  &.selected {
    color: #fff;

    button:not(.dropdown-item) {
      color: #fff;
    }
  }

  &.non-restorable {
    color: #ccc;
    cursor: not-allowed;

    &:hover {
      color: #ccc;
    }
  }

  .editor {
    line-height: 1;
  }
`;
